import { normalizeLockKey } from '@/lib/brand/resolve-brand-kit-chain';

/**
 * BI-5 hard guardrail chokepoint. Called at every post-edit write path so
 * franchise/enterprise HQ-locked fields cannot be overridden through any
 * surface (post editor, agent tools, media studio).
 *
 * Lock sets come ONLY from `resolveEffectiveLockedFields` (server-side,
 * service-role) — never from a client-supplied request body. Callers pass
 * the INHERITED (ancestor-only) enforcement set per the BI-5 phase-pinned
 * semantic: a kit's own `lockedFields` bind its descendants, never itself.
 */
export type EditGuardResult =
  { allowed: true } | { allowed: false; reason: string; lockedField: string };

/** Vocabulary for edit intents. BI-5-02 maps route editModes/presets onto
 *  these per its intent mapping table; BI-5-03 maps client actions
 *  (e.g. logo-slide manual swap → 'logo-swap'). */
export type EditIntentKind =
  | 'recolor'
  | 'logo-swap'
  | 'logo-overlay'
  | 'background'
  | 'manual-media-swap'
  | 'caption-rewrite'
  | 'generic';

export interface EditIntent {
  kind?: EditIntentKind;
  /** Proposed caption text — enables the deterministic disclaimer-verbatim check. */
  proposedCaption?: string;
  /** Resolved locked disclaimer value (from resolveEffectiveLockedFields / resolved kit). */
  disclaimerText?: string | null;
}

const COLOR_LOCK_KEYS = ['primaryColor', 'secondaryColor', 'accentColor'] as const;

export function assertEditAllowed(args: {
  /** INHERITED (ancestor-only) enforcement lock set per the phase-pinned semantic
   *  (camelCase or legacy snake_case — normalized internally). This guard keys
   *  purely off the resolved kit lock set, never a subscription/plan level;
   *  NEVER pass a kit's own locks for the kit-owner's own edits. */
  lockedFields: string[];
  field: 'caption' | 'media';
  intent?: EditIntent;
}): EditGuardResult {
  const locks = new Set(args.lockedFields.map(normalizeLockKey));
  if (locks.size === 0) return { allowed: true };

  if (args.field === 'media') {
    if (args.intent?.kind === 'recolor') {
      const lockedColorKey = COLOR_LOCK_KEYS.find((key) => locks.has(key));
      if (lockedColorKey) {
        return {
          allowed: false,
          reason:
            'Brand colors are locked by your corporate brand kit — recolor edits aren’t allowed.',
          lockedField: lockedColorKey,
        };
      }
    }
    if (
      (args.intent?.kind === 'logo-swap' || args.intent?.kind === 'logo-overlay') &&
      locks.has('logoUrl')
    ) {
      return {
        allowed: false,
        reason: 'Your logo is locked by your corporate brand kit — logo edits aren’t allowed.',
        lockedField: 'logoUrl',
      };
    }
    return { allowed: true };
  }

  // field === 'caption'
  if (locks.has('disclaimerText') && typeof args.intent?.proposedCaption === 'string') {
    const lockedDisclaimer = args.intent.disclaimerText?.trim();
    if (lockedDisclaimer) {
      if (!args.intent.proposedCaption.includes(lockedDisclaimer)) {
        return {
          allowed: false,
          reason: 'This caption must include the corporate legal disclaimer verbatim.',
          lockedField: 'disclaimerText',
        };
      }
    }
  }
  return { allowed: true };
}
